import { CSSProperties } from "react"
import { toast, ToastContainer, ToastContent, ToastOptions } from "react-toastify"
import styled from "styled-components"
import { colors } from "../DEPRECATED/colors"
import { CloseStrokeIcon } from "../icons"
import { getCssFontStyles } from "../typography"

type toasterTextPromise = {
  pending?: string
  success: string
  error: string
}

const defaultStyle = (options?: ToastStyleOptions): CSSProperties => {
  const style: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: colors.grey.$600,
    color: colors.white,
    width: "fit-content",
    minWidth: "var(--toastify-toast-width)",
    margin: "0 auto",
  }

  if (options?.forceOneLine) {
    style.textWrap = "nowrap"
    style.whiteSpace = "nowrap"
  }

  return style
}

export type ToastStyleOptions = {
  forceOneLine?: boolean
}

export const Toaster = {
  info: (text: ToastContent, args?: ToastOptions, styleOptions?: ToastStyleOptions) =>
    toast(text, {
      style: { ...defaultStyle(styleOptions) },
      ...args,
    }),
  success: (text: ToastContent, args?: ToastOptions, styleOptions?: ToastStyleOptions) =>
    toast(text, {
      style: { ...defaultStyle(styleOptions), background: colors.primary.$200 },
      ...args,
    }),
  error: (text: ToastContent, args?: ToastOptions, styleOptions?: ToastStyleOptions) =>
    toast(text, {
      style: { ...defaultStyle(styleOptions), background: colors.danger.$200 },
      ...args,
    }),
  promise: (
    promise: Promise<unknown>,
    text: toasterTextPromise,
    args?: ToastOptions,
    styleOptions?: ToastStyleOptions,
  ) => {
    toast.promise(
      promise,
      {
        pending: text.pending,
        success: text.success,
        error: {
          render() {
            return text.error
          },
        },
      },
      { style: defaultStyle(styleOptions), ...args },
    )
  },
}

const CloseButtonContainer = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;
  display: flex;
  align-items: center;
`

const CloseButton = ({ closeToast }: { closeToast: (e: React.MouseEvent<HTMLElement>) => void }) => {
  return (
    <CloseButtonContainer onClick={closeToast}>
      <CloseStrokeIcon />
    </CloseButtonContainer>
  )
}

const StyledContainer = styled(ToastContainer)`
  pointer-events: auto;

  // Override env(safe-area-inset-left) which causes centering issues
  // Only apply when position is top-center (the default for this provider)
  &.Toastify__toast-container--top-center {
    left: 50% !important;
    transform: translateX(-50%);
  }

  // https://styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity
  &&& .Toastify__toast {
    min-height: 2.75rem;
    background: ${({ theme }) => theme.colors.grey.$600};
    color: ${({ theme }) => theme.colors.white};
    border-radius: 0.5rem;
    padding: 0 0.5rem;
  }
  .Toastify__close-button {
    cursor: pointer;
    opacity: 1;
    color: ${({ theme }) => theme.colors.white};
    align-self: unset;
    padding-right: 0.5rem;
    & > svg {
      height: 1.25rem;
      width: 1.25rem;
      margin-top: 0.25rem;
    }
  }
  .Toastify__toast-body {
    ${getCssFontStyles("body-4-medium")}
    & > div {
      text-align: center;
    }
  }
`

export const ToastProvider = () => {
  return (
    <StyledContainer
      position="top-center"
      autoClose={5000}
      hideProgressBar={true}
      closeButton={CloseButton}
      newestOnTop={true}
      pauseOnHover
      closeOnClick={false}
      pauseOnFocusLoss
    />
  )
}
